import {
  Box,
  Typography,
  Button,
  IconButton,
  Checkbox,
  Dialog,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import TextField from '@mui/material/TextField';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SectionWrapper from '../SectionWrapper';
import { serviceProviderService } from '../../services';
import { ServiceProviderDetail, ContractPackage, Document } from '../../types';
import { useNotification } from '../../context/NotificationContext';
import { IMAGES } from '../../constants/assets';
import ContextMenu, { MenuAction } from '../common/ContextMenu';
import FileUploader from '../common/FileUploader';
import ContractTable from '../common/ContractTable';
import { dispatchProviderUpdate } from '../../utils/events';
import { formatDate } from '../../services/help';
import Tooltip from '@mui/material/Tooltip';
import { refreshAll } from '../../services/refreshService';
import { createZipFromFiles } from '../../utils/zip';
import AnalysisCostDialog from '../common/AnalysisCostDialog';
import { useApiAnalysisCost } from '../../hooks/useApiAnalysisCost';
import { useCreditInfo } from '../../hooks/useCreditInfo';

// Constants

// Types
interface DienstleisterDetailParams extends Record<string, string | undefined> {
  dienstleisterId: string;
}

interface ContractForTable {
  id: string;
  title: string;
  status: string;
  statusCode?: string; // Roh-Status-Code vom Backend (z.B. 'complete', 'open')
  note: string;
  createdAt: string;
  type: 'pdf' | 'docx' | 'txt';
  documentsCount: number;
  isCritical?: boolean;
}

// Hilfsfunktion: Prüft ob Status eine aktive Bearbeitung darstellt
const isProcessingStatus = (status: string): boolean => {
  return status === 'pending' || status === 'analyzing' || status.startsWith('qs_');
};

const DienstleisterDetail: React.FC = () => {
  const { t } = useTranslation();
  const { dienstleisterId } = useParams<DienstleisterDetailParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  // Status Übersetzungen - moved inside component to use t()
  const getStatusText = useCallback((status: string): string => {
    switch (status.toLowerCase()) {
      case 'open':
        return t('common:status.open');
      case 'pending':
        return t('common:status.pending');
      case 'analyzing':
        return t('common:status.analyzing');
      case 'complete':
        return t('common:status.complete');
      case 'failed':
        return t('common:status.failed');
      default:
        return status.startsWith('qs_') ? t('common:status.qsInProgress') : status;
    }
  }, [t]);

  // State
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [provider, setProvider] = useState<ServiceProviderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [editingContractId, setEditingContractId] = useState<string | null>(null);
  const [editingContractTitle, setEditingContractTitle] = useState('');
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [isCritical, setIsCritical] = useState(true);
  const [uploadTab, setUploadTab] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isCreatingService, setIsCreatingService] = useState(false);
  const [showCostDialog, setShowCostDialog] = useState(false);

  // 📊 Credit-Information laden
  const creditInfo = useCreditInfo();

  // Memoized values
  const isValidParams = useMemo(() => Boolean(dienstleisterId), [dienstleisterId]);

  const contractPackages: ContractPackage[] = useMemo(() =>
    provider?.contractPackages ||
    provider?.individualContracts?.map(contract => ({
      _id: contract._id,
      title: contract.title,
      createdAt: contract.uploadDate,
      status: contract.status,
      exitCode: (contract as any).exitCode,
      note: contract.note,
      isCritical: (contract as any).isCritical === undefined ? true : (contract as any).isCritical,
      documents: contract.documents || []
    })) || []
    , [provider]);

  const contractsForTable: ContractForTable[] = useMemo(() =>
    contractPackages.map(contractPackage => ({
      id: contractPackage._id,
      title: contractPackage.title,
      status: contractPackage.status,
      // Roh-Status zusätzlich mitgeben, damit Editierbarkeit korrekt geprüft werden kann
      statusCode: contractPackage.status,
      exitCode: (contractPackage as any).exitCode,
      note: contractPackage.note || '',
      createdAt: contractPackage.createdAt,
      type: (contractPackage.documents?.[0]?.type || 'pdf') as 'pdf' | 'docx' | 'txt',
      documentsCount: contractPackage.documents?.length || 0,
      isCritical: contractPackage.isCritical
    }))
    , [contractPackages]);

  const hasSelectedItems = useMemo(() => selectedItems.length > 0, [selectedItems]);

  // Bestimme verfügbare Aktionen für ausgewählte Dienstleistungen
  const selectedAnalysisActions = useMemo(() => {
    if (selectedItems.length === 0) return {
      canAnalyze: false,
      canCancel: false,
      runningCount: 0,
      hasDocuments: true,
      contractsWithoutDocs: 0
    };

    const selectedContracts = contractPackages.filter(contract => selectedItems.includes(contract._id));

    const analyzableCount = selectedContracts.filter(contract =>
      (contract.status === 'open' || contract.status === 'failed') &&
      contract.documents && contract.documents.length > 0
    ).length;

    const contractsWithoutDocs = selectedContracts.filter(contract =>
      !contract.documents || contract.documents.length === 0
    ).length;

    const pendingCount = selectedContracts.filter(contract =>
      contract.status === 'pending'
    ).length;

    const analyzingCount = selectedContracts.filter(contract =>
      contract.status === 'analyzing'
    ).length;

    // QS-Status zählen (alle qs_ Status sind wie analyzing zu behandeln)
    const qsCount = selectedContracts.filter(contract =>
      contract.status.startsWith('qs_')
    ).length;

    const runningCount = selectedContracts.filter(contract =>
      isProcessingStatus(contract.status)
    ).length;

    return {
      canAnalyze: analyzableCount > 0,
      canCancel: pendingCount > 0, // Nur pending Services können abgebrochen werden
      runningCount: pendingCount, // Zeige nur pending Count im Button
      pendingCount,
      analyzingCount,
      qsCount, // Neue Kategorie für QS-Status
      totalRunningCount: runningCount, // Für andere Zwecke falls benötigt
      hasDocuments: contractsWithoutDocs === 0,
      contractsWithoutDocs
    };
  }, [selectedItems, contractPackages]);

  // Kostenschätzung für Bulk-Analyse - filtere nur analysierbare Verträge
  const selectedContractsData = useMemo(() => {
    return contractPackages
      .filter(contract => selectedItems.includes(contract._id))
      .map(contract => ({
        id: contract._id,
        status: contract.status,
        isCritical: contract.isCritical
      }));
  }, [contractPackages, selectedItems]);

  // API-basierte Kostenschätzung 
  const apiCostEstimation = useApiAnalysisCost({
    analysisType: 'multiple',
    providerId: dienstleisterId || '',
    contractIds: selectedItems
  });

  // Trigger Kostenschätzung wenn Dialog geöffnet wird
  useEffect(() => {
    if (showCostDialog && selectedItems.length > 0) {
      apiCostEstimation.refetch();
    }
  }, [showCostDialog, selectedItems.length, apiCostEstimation.refetch]);

  // Data fetching
  const fetchProviderData = useCallback(async () => {
    if (!isValidParams) {
      setError(t('common:messages.loadError'));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId!);
      setProvider(data);
      setError(null);
    } catch (error: any) {
      const errorMessage = t('common:messages.loadError');
      setError(errorMessage);
      setProvider(null);
    } finally {
      setLoading(false);
    }
  }, [dienstleisterId, isValidParams, t]);

  // Effekt: immer bei Änderung der ID neu laden
  useEffect(() => {
    setError(null);
    setProvider(null);
    setLoading(true);
    fetchProviderData();
    setSelectedItems([]);
  }, [dienstleisterId]);

  // Reset edit mode when route changes or component unmounts
  useEffect(() => {
    setEditMode(false);
    setEditedTitle('');
  }, [location.pathname]);

  // Verwende die neue contractPackages Struktur wenn verfügbar, sonst Fallback auf alte Struktur
  const allContractPackages: ContractPackage[] = contractPackages;

  const handleSelectItem = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    event.stopPropagation();
    const newSelectedItems = event.target.checked
      ? [...selectedItems, id]
      : selectedItems.filter(item => item !== id);
    setSelectedItems(newSelectedItems);
  };

  const handleEditTitle = () => {
    // Dienstleister-Umbenennen nur erlauben, wenn alle zugehörigen Vertragswerke 'open' sind
    // Verwende den Roh-Status aus contractPackages (nicht den angezeigten Text)
    const hasLockedContracts = contractPackages.some(cp => cp.status !== 'open');
    if (hasLockedContracts) {
      showNotification(t('providers:messages.renameOnlyOpen'), 'error');
      return;
    }
    setEditMode(true);
    setEditedTitle(provider?.title || '');
  };

  const handleCreateService = () => {
    if (selectedFiles.length === 0) {
      showNotification(t('providers:messages.selectFileFirst'), 'error');
      return;
    }
    if (isCreatingService) {
      return; // Verhindert mehrfache Klicks
    }
    handleFileSelect(selectedFiles);
  };

  const handleSaveTitle = async () => {
    if (!dienstleisterId || !editedTitle) return;
    const result = await serviceProviderService.editServiceProviderTitle(dienstleisterId, editedTitle);
    if (result.status === 'success') {
      showNotification(result.message, 'success');

      // Sofortiges Update für die Sidebar via Event
      dispatchProviderUpdate(dienstleisterId, { title: editedTitle });

      // Lade die Daten neu, um den neuen Titel anzuzeigen
      const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
      refreshAll();
      setProvider(data);
    } else {
      showNotification(result.message, 'error');
    }
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedTitle(provider?.title || '');
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>, contracts: any) => {
    if (event.target.checked) {
      setSelectedItems(contracts.map((contract: any) => contract.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleCancelSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const handleCancelUpload = useCallback(() => {
    setIsUploadDialogOpen(false);
    setNewServiceName('');
    setIsCritical(true);
    setUploadTab(0);
    setSelectedFiles([]);
    setIsCreatingService(false);
  }, []);



  const handleStartAnalysisConfirmed = useCallback(async () => {
    if (!dienstleisterId) return;

    try {
      const result = await serviceProviderService.analyzeAllIndividualContracts(dienstleisterId, selectedItems);
      if (result.status === 'success') {
        showNotification(t('providers:messages.analyzeSuccess'), 'success');
        // Nach erfolgreichem Start: Daten neu laden um korrekte Status zu zeigen
        const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
        setProvider(data);
        refreshAll();

        // Nach 1,5 Sekunden nochmals aktualisieren für finale Status
        setTimeout(async () => {
          try {
            const updatedData = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
            setProvider(updatedData);
            refreshAll();
          } catch (error) {
            console.error('Error updating data after delay:', error);
          }
        }, 1500);
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error starting analysis:', error);
      showNotification(t('providers:messages.analyzeError'), 'error');
    }
  }, [dienstleisterId, selectedItems, showNotification, t]);

  const handleStartAnalysis = useCallback(() => {
    setShowCostDialog(true);
  }, []);

  const handleDeleteSelection = async () => {
    if (!dienstleisterId) return;
    const result = await serviceProviderService.deleteMultipleIndividualContracts(dienstleisterId, selectedItems);
    if (result.status === 'success') {
      showNotification(result.message, 'success');
      // Lade die Daten neu
      const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
      refreshAll();
      setProvider(data);
      setSelectedItems([]); // <--- Auswahl zurücksetzen!
    } else {
      showNotification(result.message, 'error');
    }
  };



  const handleRename = (contractId: string) => {
    const contractPackage = contractPackages.find(v => v._id === contractId);
    if (contractPackage) {
      // Umbenennen nur erlauben, wenn Status 'open' ist
      if (contractPackage.status !== 'open') {
        showNotification(t('providers:messages.renameOnlyOpenService'), 'error');
        return;
      }
      setEditingContractId(contractId);
      setEditingContractTitle(contractPackage.title);
    }
  };

  const handleSaveContractTitle = async (contractId: string) => {
    if (!dienstleisterId || !editingContractTitle) return;
    const result = await serviceProviderService.editIndividualContractTitle(dienstleisterId, contractId, editingContractTitle);
    if (result.status === 'success') {
      showNotification(result.message, 'success');
      // Lade die Daten neu
      const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
      setProvider(data);
    } else {
      showNotification(result.message, 'error');
    }
    setEditingContractId(null);
  };

  const handleCancelContractEdit = () => {
    setEditingContractId(null);
    setEditingContractTitle('');
  };

  const handleDownloadResults = async (contractId: string) => {
    if (!dienstleisterId) return;
    try {
      // Lade Analyseergebnisse als Excel-Datei herunter
      await serviceProviderService.downloadContractResultsExcel(dienstleisterId, contractId);
      showNotification(t('providers:messages.downloadResultsSuccess'), 'success');
    } catch (error) {
      console.error('Error downloading analysis results:', error);
      showNotification(t('providers:messages.downloadResultsError'), 'error');
    }
  };

  const handleDownloadAddendum = async (contractId: string) => {
    if (!dienstleisterId) return;
    try {
      // Lade Ergänzungsvereinbarung als Word-Datei herunter
      await serviceProviderService.downloadContractAddendum(dienstleisterId, contractId);
      showNotification(t('providers:messages.downloadAddendumSuccess'), 'success');
    } catch (error) {
      console.error('Error downloading addendum:', error);
      showNotification(t('providers:messages.downloadAddendumError'), 'error');
    }
  };

  const handleFileSelect = async (files: File[]) => {
    if (!dienstleisterId || files.length === 0) return;

    // Für neue Dienstleistungen: Validierung für Tab 0
    if (uploadTab === 0 && !newServiceName.trim()) {
      showNotification(t('providers:messages.enterServiceName'), 'error');
      return;
    }

    // 🔒 SICHERE DATEI-VALIDIERUNG
    let validationResult;

    if (uploadTab === 0) {
      // Einzelne Dokumente (PDF, DOCX, TXT)
      const { validateDocuments } = await import('../../utils/fileValidation');
      validationResult = await validateDocuments(files);
    } else {
      // Bulk-Upload: ZIP oder Sammlung von Dateien
      if (files.length === 1 && files[0].type === 'application/zip') {
        const { validateZipFile } = await import('../../utils/fileValidation');
        validationResult = await validateZipFile(files[0]);
      } else {
        // Mehrere Dateien - validiere einzeln
        const { validateDocuments } = await import('../../utils/fileValidation');
        validationResult = await validateDocuments(files);
      }
    }

    if (!validationResult.isValid) {
      showNotification(
        t('providers:messages.fileSecurityError', { error: validationResult.error }),
        'error'
      );
      return;
    }

    setIsCreatingService(true);

    try {
      let result;

      if (uploadTab === 0) {
        // Einzelne Dienstleistung mit einem oder mehreren Dokumenten
        result = await serviceProviderService.addMultipleIndividualContracts(
          dienstleisterId,
          files,
          newServiceName.trim(),
          isCritical
        );

        console.log('New service:', {
          name: newServiceName,
          isCritical,
          fileCount: files.length,
          files: files.map(f => f.name)
        });
      } else {
        // Mehrere Dienstleistungen via ZIP oder Ordner
        let zipFile: File;

        if (files.length === 1 && files[0].type === 'application/zip') {
          // User hat bereits eine ZIP-Datei hochgeladen
          zipFile = files[0];
        } else {
          // Ordner oder Sammlung von Dateien -> zippen
          zipFile = await createZipFromFiles(files, 'dienstleistungen.zip');
        }

        console.log('Bulk-Upload ZIP created:', zipFile);
        result = await serviceProviderService.bulkUploadContracts(dienstleisterId, zipFile);
      }

      if (result.status === 'success') {
        showNotification(result.message, 'success');
        // Lade die Daten neu
        const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
        refreshAll();
        setProvider(data);
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Service creation error:', error);
      showNotification(t('providers:messages.createServiceError'), 'error');
    } finally {

      setIsUploadDialogOpen(false);
      setIsCreatingService(false);
      setSelectedFiles([]);
      setNewServiceName('');
    }
  };

  const handleDownloadContract = async (contractId: string) => {
    if (!dienstleisterId) return;
    try {
      const blob = await serviceProviderService.downloadIndividualContract(dienstleisterId, contractId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contractPackage = contractPackages.find(v => v._id === contractId);
      a.download = `${contractPackage?.title || t('providers:service.title')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading:', error);
      showNotification(t('providers:messages.downloadError'), 'error');
    }
  };

  const handleAnalyze = async (contractId: string) => {
    if (!dienstleisterId) return;

    // Prüfe ob Dokumente vorhanden sind
    const contract = contractPackages.find(c => c._id === contractId);
    if (!contract?.documents || contract.documents.length === 0) {
      showNotification(
        t('providers:messages.analyzeNoDocuments', { title: contract?.title || 'Unknown' }),
        'error'
      );
      return;
    }

    try {
      const result = await serviceProviderService.analyzeIndividualContract(dienstleisterId, contractId);
      if (result.status === 'success') {
        showNotification(t('providers:messages.analyzeSuccess'), 'success');
        // Nach erfolgreichem Start: Daten neu laden
        const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
        setProvider(data);
        refreshAll();

        // Nach 1,5 Sekunden nochmals aktualisieren für finale Status
        setTimeout(async () => {
          try {
            const updatedData = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
            setProvider(updatedData);
            refreshAll();
          } catch (error) {
            console.error('Error updating data after delay:', error);
          }
        }, 1500);
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error starting analysis:', error);
      showNotification(t('providers:messages.analyzeError'), 'error');
    }
  };

  const handleCancelAnalysis = async (contractId: string) => {
    if (!dienstleisterId) return;

    try {
      const result = await serviceProviderService.cancelIndividualContractAnalysis(dienstleisterId, contractId);

      if (result.status === 'success') {
        showNotification(result.message, 'success');
        // Sofortiges Update nach dem Abbrechen
        const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
        setProvider(data);
      } else {
        // Verbesserte Fehlermeldung basierend auf dem Status
        const contract = contractPackages.find(c => c._id === contractId);
        if (contract?.status === 'analyzing') {
          showNotification(t('providers:messages.cancelAnalyzingError'), 'error');
        } else {
          showNotification(result.message || t('providers:messages.cancelError'), 'error');
        }
      }
    } catch (error) {
      console.error('Error cancelling analysis:', error);
      showNotification(t('providers:messages.cancelUnexpectedError'), 'error');
    }
  };

  const handleCancelSelectedAnalyses = async () => {
    if (!dienstleisterId) return;

    try {
      // Unterscheide zwischen "pending" (abbrechbar), "analyzing" (möglicherweise nicht abbrechbar) und QS-Status (nicht abbrechbar)
      const selectedContracts = contractPackages.filter(contract => selectedItems.includes(contract._id));

      const pendingContracts = selectedContracts.filter(contract => contract.status === 'pending');
      const analyzingContracts = selectedContracts.filter(contract => contract.status === 'analyzing');
      const qsContracts = selectedContracts.filter(contract => contract.status.startsWith('qs_'));

      const totalRunning = pendingContracts.length + analyzingContracts.length + qsContracts.length;

      if (totalRunning === 0) {
        showNotification(t('providers:messages.noRunningAnalyses'), 'info');
        return;
      }

      // QS-Status können nicht abgebrochen werden - informiere den User
      if (qsContracts.length > 0) {
        showNotification(
          t('providers:messages.qsCannotCancel', { count: qsContracts.length }),
          'warning'
        );
      }

      // Versuche nur pending und analyzing abzubrechen
      const cancellableContracts = [...pendingContracts, ...analyzingContracts];

      if (cancellableContracts.length === 0) {
        showNotification(t('providers:messages.noCancellable'), 'info');
        return;
      }

      const promises = cancellableContracts.map(contract =>
        serviceProviderService.cancelIndividualContractAnalysis(dienstleisterId, contract._id)
      );

      const results = await Promise.allSettled(promises);

      const successful = results.filter(result =>
        result.status === 'fulfilled' && result.value.status === 'success'
      ).length;

      const failed = results.length - successful;

      // Detaillierte Erfolgsmeldung
      if (successful > 0 && failed === 0) {
        showNotification(
          t('providers:messages.allCancelledSuccess', { count: successful }),
          'success'
        );
      } else if (successful > 0 && failed > 0) {
        showNotification(
          t('providers:messages.partialCancelSuccess', { successful, total: results.length, failed }),
          'warning'
        );
      } else {
        // Alle fehlgeschlagen - gib spezifischen Grund an
        if (analyzingContracts.length > 0 && pendingContracts.length === 0) {
          showNotification(
            t('providers:messages.cancelAnalyzingError'),
            'error'
          );
        } else {
          showNotification(
            t('providers:messages.cancelTooLateError'),
            'error'
          );
        }
      }

      // Lade die Daten neu wenn mindestens eine erfolgreich war
      if (successful > 0) {
        const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
        setProvider(data);
      }
    } catch (error) {
      console.error('Error cancelling selected analyses:', error);
      showNotification(t('providers:messages.cancelUnexpectedError'), 'error');
    }
  };

  const handleDeleteContract = async (contractId: string) => {
    if (!dienstleisterId || !contractId) return;
    const result = await serviceProviderService.deleteIndividualContract(dienstleisterId, contractId);
    if (result.status === 'success') {
      const data = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
      refreshAll();
      setProvider(data);
      showNotification(result.message, 'success');
    } else {
      showNotification(result.message, 'error');
    }
  };

  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    setIsUploadDialogOpen(true);
  };

  const handleContractClick = (contractId: string) => {
    // Navigiere zu der neuen ContractPackage-Detailseite
    navigate(`/dienstleister/${dienstleisterId}/dienstleistung/${contractId}`);
  };

  // Loading state
  if (loading && !error) {
    return <Box sx={{ p: 3 }}>{t('common:status.loading')}</Box>;
  }

  // Error state or Not found state - both show the same styled alert
  if (!provider) {
    return (
      <Alert
        severity="error"
        sx={{ m: 3 }}
        action={
          <Button color="inherit" size="small" onClick={() => navigate('/dienstleister')}>
            {t('common:buttons.back')}
          </Button>
        }
      >
        {error || t('providers:messages.providerNotFound')}
      </Alert>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', mb: '10rem' }}>
      {/* Dienstleister Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: '6.25rem', justifyContent: 'space-between' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          '&:hover .edit-icon': {
            opacity: 1
          }
        }}>
          {editMode ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={editedTitle}
                onChange={e => setEditedTitle(e.target.value)}
                size="small"
                inputProps={{ 'data-testid': 'provider-detail-header-title-input' } as any}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '2.125rem',
                    color: 'primary.dark',
                    paddingLeft: '1.5rem',
                    paddingTop: '0',
                    paddingBottom: '0',
                  },
                  '& .MuiOutlinedInput-root': {
                  }
                }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveTitle();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
              />
              <IconButton
                size="small"
                onClick={handleSaveTitle}
                data-testid="provider-detail-header-save-title-button"
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                data-testid="provider-detail-header-cancel-edit-button"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <>
              <Typography
                variant="h4"
                sx={{
                  color: 'primary.dark',
                  paddingLeft: '1.5rem',
                }}
              >
                {provider?.title}
              </Typography>
              <EditIcon
                className="edit-icon"
                data-testid="provider-detail-header-edit-title-button"
                sx={{
                  fontSize: '1rem',
                  opacity: 0,
                  cursor: 'pointer',
                  color: 'primary.blue',
                  transition: 'opacity 0.2s'
                }}
                onClick={handleEditTitle}
              />
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
        </Box>
      </Box>

      {/* Dienstleistungen Sektion */}
      <Box sx={{ mb: 4, mt: '3.25rem' }}>
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography>{t('providers:detail.services')}</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              data-testid="provider-detail-add-service-button"
              sx={{ textTransform: 'none' }}
            >
              {t('providers:detail.addService')}
            </Button>
          </Box>
        </Box>

        <ContractTable
          contracts={contractsForTable}
          selectedItems={selectedItems}
          editingContractId={editingContractId}
          editingContractTitle={editingContractTitle}
          selectedRowId={selectedRowId}
          providerId={dienstleisterId}
          enablePolling={true}
          pollingInterval={2000} // 2 Sekunden
          enableFolderDragDrop={true} // Aktiviere Drag-and-Drop für Ordner
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
          onRename={handleRename}
          onSaveContractTitle={handleSaveContractTitle}
          onCancelContractEdit={handleCancelContractEdit}
          setEditingContractTitle={setEditingContractTitle}
          onRowClick={handleContractClick}
          onContractsUpdate={async (newContracts) => {


            // Aktualisiere den provider State mit den neuen Contract-Daten
            setProvider(prevProvider => {
              if (!prevProvider) return prevProvider;

              const updatedContractPackages = newContracts.map(contract => {
                // Status ist bereits normalisiert (englisch) vom Backend
                const apiStatus = contract.status.toLowerCase();

                // Finde das entsprechende Contract Package im bisherigen Provider um Dokumente zu erhalten
                const existingContract = prevProvider.contractPackages?.find(cp => cp._id === contract.id);

                return {
                  _id: contract.id,
                  title: contract.title,
                  createdAt: contract.createdAt,
                  status: apiStatus,
                  note: contract.note || '',
                  isCritical: contract.isCritical ?? true,
                  // Behalte vorhandene Dokumente; echter Re-Fetch unten synchronisiert die Liste
                  documents: existingContract?.documents || []
                };
              });

              return {
                ...prevProvider,
                contractPackages: updatedContractPackages
              };
            });

            // Sofortige Korrektur: Wenn neue Zähler > 0 aber lokale Dokumente (noch) leer sind,
            // lade den Dienstleister erneut, damit Dokumente und Zähler konsistent sind
            try {
              const needsRefetch = newContracts.some(c => {
                const existing = provider?.contractPackages?.find(cp => cp._id === c.id);
                const existingDocsLen = existing?.documents?.length || 0;
                return (c.documentsCount || 0) > 0 && existingDocsLen === 0;
              });
              if (needsRefetch && dienstleisterId) {
                const fresh = await serviceProviderService.getServiceProviderDetails(dienstleisterId);
                setProvider(fresh);
              }
            } catch (e) {
              console.warn('Refetch after upload failed:', e);
            }
          }}
          onError={(error) => {
            console.error('Contract polling error:', error);

            // Prüfe auf "Dienstleister nicht mehr vorhanden" und setze Error-State
            if (error === 'Dienstleister nicht mehr vorhanden') {
              setError(t('providers:messages.providerNotFound'));
              setProvider(null);
            } else {
              showNotification(error, 'error');
            }
          }}
          onSuccess={(message) => {
            showNotification(message, 'success');
          }}
          // Context Menu Handlers
          onAnalyze={handleAnalyze}
          onCancelAnalysis={handleCancelAnalysis}
          onDeleteContract={handleDeleteContract}
          onDownloadResults={handleDownloadResults}
          onDownloadAddendum={handleDownloadAddendum}
        />
      </Box>

      {/* Auswahl-Leiste für ausgewählte Items */}
      {selectedItems.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Typography>
              {t('providers:sidebar.selected', { count: selectedItems.length })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {/* Zeige "Analysen abbrechen" wenn laufende Analysen vorhanden, sonst "Abbrechen" */}
            {selectedAnalysisActions.canCancel ? (
              <Tooltip title={t('providers:messages.cancelPendingTooltip')}>
                <Box
                  onClick={handleCancelSelectedAnalyses}
                  data-testid="provider-detail-cancel-analyses-button"
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#1976d2',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}
                >
                  <CloseIcon sx={{ fontSize: '1.4rem' }} />
                  {t('providers:actions.cancelPending', { count: selectedAnalysisActions.runningCount })}
                </Box>
              </Tooltip>
            ) : (
              <Box
                onClick={handleCancelSelection}
                data-testid="provider-detail-selection-cancel-button"
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'primary.dark',
                  fontWeight: 500,
                  fontSize: '1rem'
                }}
              >
                <CloseIcon sx={{ fontSize: '1.4rem' }} />
                {t('common:buttons.cancel')}
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onClick={() => {
                if (selectedItems.length === contractsForTable.length) {
                  setSelectedItems([]);
                } else {
                  setSelectedItems(contractsForTable.map(contract => contract.id));
                }
              }}
              data-testid="provider-detail-selection-select-all"
            >
              <Checkbox
                checked={selectedItems.length === contractsForTable.length}
                data-testid="provider-detail-select-all-checkbox"
                sx={{
                  color: 'primary.blue',
                  '&.Mui-checked': {
                    color: 'primary.blue',
                  },
                }}
              />
              <Typography>
                {t('providers:actions.selectAll')}
              </Typography>
            </Box>
            <Tooltip
              title={
                selectedAnalysisActions.contractsWithoutDocs > 0
                  ? t('providers:analysis.noDocuments', { count: selectedAnalysisActions.contractsWithoutDocs })
                  : !selectedAnalysisActions.canAnalyze && selectedItems.length > 0
                    ? t('providers:messages.noAnalyzableSelected')
                    : t('providers:messages.startAnalysisTooltip')
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleStartAnalysis}
                  disabled={!selectedAnalysisActions.canAnalyze}
                  data-testid="provider-detail-start-analysis-button"
                  sx={{ textTransform: 'none' }}
                >
                  {t('providers:actions.analyzeSelected')}
                </Button>
              </span>
            </Tooltip>
            <Box
              onClick={handleDeleteSelection}
              data-testid="provider-detail-selection-delete-button"
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#FF0004',
                fontWeight: 500,
                fontSize: '1rem'
              }}
            >
              <img src={IMAGES.DELETE} alt={t('common:buttons.delete')} style={{ width: '1rem', height: '1rem' }} />
              {t('common:buttons.delete')}
            </Box>
          </Box>
        </Box>
      )}

      {/* Upload Dialog für Dienstleistungen */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={handleCancelUpload}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '1250px',
            minHeight: '700px',
            boxShadow: 'none',
            p: '1rem',
            borderRadius: '12px',
            border: 'none',
            borderBottom: 'none',
            marginTop: '60px',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: '2rem',
          py: '1rem',
          zIndex: 10
        }}>
          <Box
            onClick={() => setUploadTab(0)}
            data-testid="provider-detail-upload-tab-single"
            sx={{
              py: 2,
              cursor: 'pointer',
              textAlign: 'center',
              color: uploadTab === 0 ? '0F1F44' : 'text.secondary',
              fontWeight: 400,
              fontSize: '2.1875rem',
              marginRight: '4px',
              '&:hover': {
                color: 'text.primary',
              },
              '&:after': {
                content: '""',
                display: 'block',
                width: '100%',
                height: uploadTab === 0 ? '1px' : '0',
                backgroundColor: 'primary.blue',
              }
            }}
          >
            {t('providers:detail.singleUpload')}
          </Box>
          <Box
            onClick={() => setUploadTab(1)}
            data-testid="provider-detail-upload-tab-bulk"
            sx={{
              py: 2,
              cursor: 'pointer',
              textAlign: 'center',
              color: uploadTab === 1 ? '0F1F44' : 'text.secondary',
              fontWeight: 400,
              fontSize: '2.1875rem',
              '&:hover': {
                color: 'text.primary',
              },
              '&:after': {
                content: '""',
                display: 'block',
                width: '100%',
                height: uploadTab === 1 ? '1px' : '0',
                backgroundColor: 'primary.blue',
              }
            }}
          >
            {t('providers:detail.multipleUpload')}
          </Box>
        </Box>

        <Box sx={{ px: 5, position: 'relative', backgroundColor: 'background.paper', borderBottom: 'none' }}>
          {/* Tab Content */}
          {uploadTab === 0 && (<>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, mt: 2 }}>
              {t('providers:upload.singleDescription')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              {/* FileUploader Links */}
              <Box sx={{ flex: 1 }}>
                <FileUploader
                  onFileSelect={(newFiles) => {
                    // BUG FIX: Accumulate files instead of replacing them
                    // Filter out duplicates based on file name
                    setSelectedFiles(prevFiles => {
                      const existingNames = new Set(prevFiles.map(f => f.name));
                      const uniqueNewFiles = newFiles.filter(f => !existingNames.has(f.name));
                      return [...prevFiles, ...uniqueNewFiles];
                    });
                  }}
                  accept={{
                    'application/pdf': ['.pdf'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                    'text/plain': ['.txt']
                  }}
                  maxSize={10485760} // 10MB pro Datei
                  title={t('providers:upload.dragFilesTitle')}
                  subtitle={t('providers:upload.dragFilesSubtitle')}
                  buttonText={t('providers:upload.selectFilesButton')}
                  multiple={true}
                  validationType="documents"
                  onValidationError={(error, rejectedFiles) => {
                    showNotification(t('providers:messages.validationError', { error }), 'error');
                  }}
                />

                {/* Ausgewählte Dateien anzeigen */}
                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">
                        {t('providers:upload.selectedFiles', { count: selectedFiles.length })}:
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setSelectedFiles([])}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        data-testid="provider-detail-upload-remove-all-files-button"
                      >
                        {t('providers:upload.removeAll')}
                      </Button>
                    </Box>
                    <Box sx={{ maxHeight: '120px', overflowY: 'auto' }}>
                      {selectedFiles.map((file, index) => (
                        <Box key={index} sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          borderRadius: 0.5
                        }}>
                          <Typography variant="body2" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '200px'
                          }}>
                            📄 {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newFiles = selectedFiles.filter((_, i) => i !== index);
                              setSelectedFiles(newFiles);
                            }}
                            sx={{ ml: 1 }}
                            data-testid={`provider-detail-upload-remove-file-${index}-button`}
                          >
                            <CloseIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('providers:upload.totalSize')}: {(selectedFiles.reduce((acc, file) => acc + file.size, 0) / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Zusätzliche Felder Rechts */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Typography variant="subtitle1">
                    {t('providers:detail.serviceName')}
                  </Typography>

                  <TextField
                    fullWidth
                    value={newServiceName}
                    onChange={(e) => setNewServiceName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newServiceName.trim() && selectedFiles.length > 0) {
                        e.preventDefault();
                        handleCreateService();
                      }
                    }}
                    variant="standard"
                    placeholder={t('providers:upload.serviceNamePlaceholder')}
                    inputProps={{ 'data-testid': 'provider-detail-upload-service-name-input' } as any}
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={isCritical}
                      onChange={(e) => setIsCritical(e.target.checked)}
                      inputProps={{ 'data-testid': 'provider-detail-upload-critical-checkbox' } as any}
                      sx={{
                        color: 'primary.blue',
                        '&.Mui-checked': {
                          color: 'primary.blue',
                        },
                      }}
                    />
                    <Typography variant="body2">
                      {t('providers:upload.isCritical')}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                    {t('providers:upload.criticalDescription')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <Box
                    style={{ display: 'flex', flex: 1, justifyContent: 'center', gap: 4, color: 'primary.dark', textTransform: 'none', fontWeight: 500, fontSize: '1rem' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={handleCancelUpload} data-testid="provider-detail-upload-cancel-button">
                      <CloseIcon sx={{ fontSize: '1.4rem' }} />
                      {t('common:buttons.cancel')}
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleCreateService}
                    disabled={!newServiceName.trim() || selectedFiles.length === 0 || isCreatingService}
                    data-testid="provider-detail-upload-create-service-button"
                    sx={{ textTransform: 'none', flex: 1 }}
                  >
                    {isCreatingService ? t('providers:upload.creating') : t('providers:detail.createService')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </>)}

          {uploadTab === 1 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4, mt: 2 }}>
                {t('providers:upload.bulkDescription')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 5, mb: 3 }}>
                {/* FileUploader Links */}
                <Box sx={{ flex: 1 }}>
                  <FileUploader
                    onFileSelect={handleFileSelect}
                    accept={{
                      'application/zip': ['.zip'],
                      // Alle Dateien zulassen, damit Ordner mit PDF/DOCX etc. gelesen werden können
                      'application/pdf': ['.pdf'],
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                      'text/plain': ['.txt']
                    }}
                    maxSize={52428800} // 50MB
                    title={t('providers:upload.dragZipTitle')}
                    subtitle={t('providers:upload.dragZipSubtitle')}
                    buttonText={t('providers:upload.selectZipButton')}
                    multiple={false}
                    directory={false}
                    validationType="zip"
                    onValidationError={(error, rejectedFiles) => {
                      showNotification(t('providers:messages.validationError', { error }), 'error');
                    }}
                  />
                </Box>

                {/* Informationen Rechts */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('providers:upload.exampleStructure')}:
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      📁 dienstleistungen.zip<br />
                      &nbsp;&nbsp;📁 IT-Support/<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;📄 dokument.pdf<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;📄 anhang.docx<br />
                      &nbsp;&nbsp;📁 Cloud-Dienst/<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;📄 hauptdokument.pdf<br />
                      &nbsp;&nbsp;📁 Software-Dienst/<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;📄 dokument.pdf<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;📄 zusatz.pdf
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    {t('providers:upload.bulkInstructions')}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {/* Kostenbestätigung Dialog */}
      <AnalysisCostDialog
        open={showCostDialog}
        onClose={() => setShowCostDialog(false)}
        onConfirm={() => {
          setShowCostDialog(false);
          handleStartAnalysisConfirmed();
        }}
        analysisType="multiple"
        contractCount={selectedItems.length}
        loading={apiCostEstimation.loading}
        error={apiCostEstimation.error}
        apiData={apiCostEstimation.data}
        creditInfo={creditInfo.isUnlimited ? null : {
          availableCredits: creditInfo.availableCredits,
          requiredCredits: apiCostEstimation.data?.totalAnalyzableContracts ?? selectedItems.length,
          remainingAfter: creditInfo.availableCredits !== null
            ? creditInfo.availableCredits - (apiCostEstimation.data?.totalAnalyzableContracts ?? selectedItems.length)
            : null
        }}
      />
    </Box>
  );
};

export default DienstleisterDetail;
